import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import CryptoJS from "crypto-js";
import { FiActivity, FiMail, FiDownload } from "react-icons/fi";
import Navbar from "../../../common/components/HeaderOpsPage/Navbar";
import Body from "../../../common/components/Body/Body";
import "./MontenidoUsage.css";

const DOMAIN = "montenido.com";
const SECRET_KEY = "1234567890abcdef1234567890abcdef";

function decryptPayload(ciphertext) {
  const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
  return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
}

function toCsv(rows) {
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = "Email,FirstName,TotalHits,UniqueEndpoints,LastSeen\n";
  const body = rows
    .map((r) =>
      [
        escape(r.Email),
        escape(r.FirstName),
        r.hits,
        r.uniqueEndpoints,
        new Date(r.lastSeen).toISOString(),
      ].join(",")
    )
    .join("\n");
  return header + body + "\n";
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const MontenidoUsage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userRole = useSelector((state) => state.login?.login?.data?.data?.[0]?.Role);
  const userEmail = (useSelector((state) => state.login?.login?.data?.data?.[0]?.Email) || "").toLowerCase();
  const authToken = localStorage.getItem("authToken");

  const ALLOWED_OPS_DOMAINS = ["@montenido.com", "@magichealth.io"];
  const isAuthorizedOpsManager =
    userRole === "Operations Manager" &&
    ALLOWED_OPS_DOMAINS.some((d) => userEmail.endsWith(d));
  const isAuthorized = userRole === "Admin" || isAuthorizedOpsManager;

  useEffect(() => {
    if (!isAuthorized) {
      setError("Access denied. This report is restricted to Admins or Operations Managers with a montenido.com or magichealth.io email.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_ANALYTICS_API}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        });
        if (!res.ok) throw new Error(`Analytics API returned ${res.status}`);
        const cipher = await res.text();
        setData(decryptPayload(cipher));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthorized, authToken]);

  const montenidoUsers = useMemo(() => {
    // Build the row set from the directory of all @montenido.com users
    // (so users with no activity still appear), then layer activity on top.
    const directory = data?.montenidoDirectory || [];
    const activityRows = data?.allUsers || data?.topUsers || [];

    // Index activity by email (lowercased), merging rows that share an email
    // but differ by AdminID/FirstName (which happens when SQL data is stale
    // or AdminIDs got reassigned).
    const activityByEmail = new Map();
    for (const u of activityRows) {
      if (!u.Email) continue;
      const key = String(u.Email).toLowerCase();
      const existing = activityByEmail.get(key);
      if (existing) {
        existing.hits += u.hits || 0;
        existing.uniqueEndpoints = Math.max(
          existing.uniqueEndpoints,
          u.uniqueEndpoints || 0
        );
        if (
          u.lastSeen &&
          (!existing.lastSeen || new Date(u.lastSeen) > new Date(existing.lastSeen))
        ) {
          existing.lastSeen = u.lastSeen;
        }
      } else {
        activityByEmail.set(key, {
          hits: u.hits || 0,
          uniqueEndpoints: u.uniqueEndpoints || 0,
          lastSeen: u.lastSeen || null,
        });
      }
    }

    const rowsFromDirectory = directory
      .filter((d) => d.Email && String(d.Email).toLowerCase().endsWith(`@${DOMAIN}`))
      .map((d) => {
        const act = activityByEmail.get(String(d.Email).toLowerCase()) || {
          hits: 0,
          uniqueEndpoints: 0,
          lastSeen: null,
        };
        return {
          AdminID: d.AdminID,
          FirstName: d.FirstName || "Unknown",
          Email: d.Email,
          hits: act.hits,
          uniqueEndpoints: act.uniqueEndpoints,
          lastSeen: act.lastSeen,
        };
      });

    // Fallback path — if the BE didn't return montenidoDirectory yet
    // (e.g. deploy hasn't propagated), fall back to filtering activityRows.
    const rows =
      rowsFromDirectory.length > 0
        ? rowsFromDirectory
        : activityRows.filter(
            (u) =>
              u.Email && String(u.Email).toLowerCase().endsWith(`@${DOMAIN}`)
          );

    return rows.sort((a, b) => {
      const ta = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
      const tb = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;
      return tb - ta;
    });
  }, [data]);

  const totals = useMemo(() => {
    const totalHits = montenidoUsers.reduce((s, u) => s + (u.hits || 0), 0);
    const activeCount = montenidoUsers.filter((u) => (u.hits || 0) > 0).length;
    return {
      totalCount: montenidoUsers.length,
      activeCount,
      totalHits,
    };
  }, [montenidoUsers]);

  const handleDownload = () => {
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    downloadCsv(`montenido-usage-${stamp}.csv`, toCsv(montenidoUsers));
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <Body>
          <div className="mu-state">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading Montenido usage…</p>
          </div>
        </Body>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Navbar />
        <Body>
          <div className="mu-state">
            <FiActivity size={48} color="#ef4444" />
            <h2>Couldn't load usage report</h2>
            <p>{error}</p>
          </div>
        </Body>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <Body>
        <div className="mu-page">
          <div className="mu-header">
            <div>
              <h1>Montenido Usage Report</h1>
              <p className="mu-subtitle">
                All users with <code>@{DOMAIN}</code> email addresses, sorted by
                most recently active. Hits reflect the last 30 days of activity
                on the analytics-instrumented endpoints.
              </p>
            </div>
            <button
              className="mu-download-btn"
              onClick={handleDownload}
              disabled={montenidoUsers.length === 0}
              type="button"
            >
              <FiDownload size={16} style={{ marginRight: 6 }} />
              Download CSV
            </button>
          </div>

          <div className="mu-stats">
            <div className="mu-stat-card">
              <div className="mu-stat-label">Montenido Users</div>
              <div className="mu-stat-value">{totals.totalCount}</div>
            </div>
            <div className="mu-stat-card">
              <div className="mu-stat-label">Active (last 30 days)</div>
              <div className="mu-stat-value">{totals.activeCount}</div>
            </div>
            <div className="mu-stat-card">
              <div className="mu-stat-label">Total Hits (last 30 days)</div>
              <div className="mu-stat-value">{totals.totalHits.toLocaleString()}</div>
            </div>
          </div>

          {montenidoUsers.length === 0 ? (
            <div className="mu-empty">
              <FiMail size={36} color="#94a3b8" />
              <p>No <code>@{DOMAIN}</code> users found in the directory.</p>
            </div>
          ) : (
            <div className="mu-table-card">
              <table className="mu-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>First Name</th>
                    <th>Total Hits</th>
                    <th>Unique Endpoints</th>
                    <th>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {montenidoUsers.map((u) => (
                    <tr key={u.AdminID}>
                      <td className="mu-email">{u.Email}</td>
                      <td>{u.FirstName}</td>
                      <td className="mu-numeric">{u.hits.toLocaleString()}</td>
                      <td className="mu-numeric">{u.uniqueEndpoints ?? "—"}</td>
                      <td>
                        {u.lastSeen
                          ? new Date(u.lastSeen).toLocaleString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mu-footnote">
            <strong>Note:</strong> This is the initial release of the Montenido
            Usage report. Activity tracking begins as of this release, so the
            30-day view will populate over time as usage accumulates.
          </div>
        </div>
      </Body>
    </div>
  );
};

export default MontenidoUsage;
